import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useCallback, useEffect } from "react";
import { useWebsites } from "@/context/WebsitesContext";
import { useSettings } from "@/context/SettingsContext";
import { useSettingsState } from "@/context/SettingsStateContext";
import useWordpressSettings from "./useWordpressSettings";

export default function usePublicationSettings() {
  const {
    setPostingFrequency,
    writingStyle,
    setWritingStyle,
    setSubjectMatters,
    setWeeklyPlanningDay,
  } = useSettings();
  const {
    setStyleInput,
    subjects,
    setSubjects,
    setIsConnecting,
    setSaving,
    setPostingDays,
  } = useSettingsState();
  const { updateWordPressPublishStatus } = useWordpressSettings();
  const { currentWebsite } = useWebsites();

  // Memoize the publication settings save handler
  const handleSavePublicationSettings = useCallback(
    async (settings: { postingDays: string[]; weeklyPlanningDay: string; isAutomatedWeeklyPlanningActive: boolean; }) => {
      if (!currentWebsite) {
        toast.error("Please select a website first");
        return;
      }

      setSaving(true);
      try {
        const formattedPostingDays = settings.postingDays.reduce(
          (acc: { day: string; count: number }[], day) => {
            const existingDay = acc.find((d) => d.day === day);
            if (existingDay) {
              existingDay.count++;
            } else {
              acc.push({ day, count: 1 });
            }
            return acc;
          },
          []
        );

        console.log("Saving publication settings:", {
          website_id: currentWebsite.id,
          posting_frequency: settings.postingDays.length,
          posting_days: formattedPostingDays,
          writing_style: writingStyle,
          subject_matters: subjects,
          weekly_planning_day: settings.weeklyPlanningDay,
          is_automated_weekly_planning_active: settings.isAutomatedWeeklyPlanningActive,
        });

        const { data: existingSettings } = await supabase
          .from("publication_settings")
          .select("id")
          .eq("website_id", currentWebsite.id)
          .order("created_at", { ascending: false })
          .limit(1);

        if (existingSettings?.[0]) {
          const { error: updateError } = await supabase
            .from("publication_settings")
            .update({
              posting_frequency: settings.postingDays.length,
              posting_days: formattedPostingDays,
              writing_style: writingStyle || "",
              subject_matters: subjects || [],
              weekly_planning_day: settings.weeklyPlanningDay,
              is_automated_weekly_planning_active: settings.isAutomatedWeeklyPlanningActive,
              updated_at: new Date().toISOString(),
            })
            .eq("id", existingSettings[0].id);

          if (updateError) throw updateError;
        } else {
          const { error: insertError } = await supabase
            .from("publication_settings")
            .insert({
              website_id: currentWebsite.id,
              posting_frequency: settings.postingDays.length,
              posting_days: formattedPostingDays,
              writing_style: writingStyle || "",
              subject_matters: subjects || [],
              weekly_planning_day: settings.weeklyPlanningDay,
              is_automated_weekly_planning_active: settings.isAutomatedWeeklyPlanningActive,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            });

          if (insertError) throw insertError;
        }

        setPostingDays(settings.postingDays);
        setPostingFrequency(settings.postingDays.length);
        setWeeklyPlanningDay(settings.weeklyPlanningDay);

        toast.success("Publication settings saved successfully");
      } catch (error) {
        console.error("Error saving publication settings:", error);
        toast.error("Failed to save publication settings");
      } finally {
        setSaving(false);
      }
    },
    [currentWebsite, writingStyle, subjects, setWeeklyPlanningDay]
  );

  const handlePublishStatusChange = async (value: string) => {
    setIsConnecting(true);
    try {
      updateWordPressPublishStatus(value);
    } catch (error) {
      console.error("Error updating publish status:", error);
      toast.error("Failed to update publish status");
    } finally {
      setIsConnecting(false);
    }
  };

  // Load publication settings when component mounts
  useEffect(() => {
    const loadPublicationSettings = async () => {
      if (!currentWebsite) return;

      try {
        console.log("Loading publication settings for website:", currentWebsite.id);

        const { data: settings, error } = await supabase
          .from("publication_settings")
          .select("*")
          .eq("website_id", currentWebsite.id)
          .order("created_at", { ascending: false })
          .limit(1);

        if (error) {
          console.error("Error loading publication settings:", error);
          return;
        }

        const currentSettings = settings?.[0];
        if (currentSettings) {
          console.log("Found existing publication settings:", currentSettings);
          const days = currentSettings.posting_days?.reduce(
            (acc: string[], day: { day: string; count: number }) => {
              return [...acc, ...Array(day.count).fill(day.day)];
            },
            []
          ) || ["monday", "wednesday", "friday"];

          setPostingDays(days);
          setPostingFrequency(days.length);

          if (currentSettings.writing_style) {
            setWritingStyle(currentSettings.writing_style);
            setStyleInput(currentSettings.writing_style);
          }
          if (currentSettings.subject_matters) {
            setSubjectMatters(currentSettings.subject_matters);
            setSubjects(currentSettings.subject_matters);
          }
          if (currentSettings.weekly_planning_day) {
            setWeeklyPlanningDay(currentSettings.weekly_planning_day);
          }
        } else {
          console.log("No existing publication settings, using defaults");
          setPostingDays(["monday", "wednesday", "friday"]);
          setPostingFrequency(3);
          setWeeklyPlanningDay("friday");
        }
      } catch (error) {
        console.error("Error loading publication settings:", error);
      }
    };

    loadPublicationSettings();
  }, [currentWebsite]);

  return {
    handleSavePublicationSettings,
    handlePublishStatusChange,
  };
}
